import {organizationStore} from "../../store/organization";
import {createApolloClient} from "../../providers/ApolloClientFactory";
import {GET_LOCATION_TYPE, GET_LOCATIONS} from "./queries";
import {
    Link,
    LoaderFunctionArgs,
    useLoaderData,
    useNavigate,
    useRevalidator,
} from "react-router-dom";
import {Location, LocationMenus} from "../../types/location";
import {Badge} from "../../components/Badge";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {eventStore} from "../../store/event";
import {userStore} from "../../store/user";
import moment from "moment/moment";
import {usePermissions} from "../auth/api";
import {
    ColDef,
    GridApi,
    SizeColumnsToContentStrategy,
} from "ag-grid-community";
import {Input} from "../../components/inputs/Input";
import {PrimaryButton, SecondaryButton} from "../../components/Button";
import {AgGridReact} from "ag-grid-react";
import Switch from "react-switch";

import {useGridEdit} from "../helpers/GridEditHelper";
import {useGridSave} from "../helpers/UseGridSave";
import TextSuggestEditor from "../helpers/TextSuggestEditorHelper";
import {UPDATE_LOCATION} from "./mutations";
import {
    GET_LIST_EVENTS_BY_ORGANIZATION,
    GET_LIST_PAYMENT_PROCESSORS_BY_ORGANIZATION,
    GET_LIST_VENDORS_BY_ORGANIZATION
} from "../../queries";
import GridEditActions from "../helpers/GridEditActions";

const client = createApolloClient();
const {getState: getUserStore} = userStore;
const {getState: getOrganizationState} = organizationStore;
const {getState: getEventStore} = eventStore;

const buildSearch = (search: string) => [
    {name: {_ilike: `%${search}%`}},
    {location_type: {_ilike: `%${search}%`}},
];

type CellRenderer = (params: {
    value:
        | string
        | number
        | boolean
        | React.ReactElement<any, string | React.JSXElementConstructor<any>>
        | React.ReactFragment
        | React.ReactPortal
        | null
        | undefined;
    data: Location;
}) => JSX.Element | null;

const linkRendererFactory = <T, >(
    permission: boolean,
    linkFor: (params: { data: Location }) => string
): CellRenderer => {
    return (params) => {
        if (params.value === undefined) return null;
        return permission ? (
            <Link to={linkFor(params)}>
                <Badge>{params.value}</Badge>
            </Link>
        ) : (
            <Badge>{params.value}</Badge>
        );
    };
};

const multiMenuLinksRenderer = (
    permission: boolean,
    linkFor: (lm: LocationMenus) => string
): CellRenderer => {
    return (params) => {
        const menus = params.data?.location_menus ?? [];
        return (
            <>
                {menus.map((lm, i) => (
                    <span key={lm.menu?.id ?? i}>
            {permission ? (
                <Link to={linkFor({...lm, menus: lm.menu})}>
                    <Badge>{lm.menu?.name}</Badge>
                </Link>
            ) : (
                <Badge>{lm.menu?.name}</Badge>
            )}
                        {i < menus.length - 1 && " "}
          </span>
                ))}
            </>
        );
    };
};

export const locationsListLoader = async ({ request }: LoaderFunctionArgs) => {
    const url = new URL(request.url);
    const search = url.searchParams.get("search");
    const rawOrganizationId = getOrganizationState().organizationId;
    const organizationId = parseInt(rawOrganizationId !== null ? rawOrganizationId.toString() : "", 10);
    const eventId = getEventStore().eventId;
    const user = getUserStore().user;

    let where: any = { _or: search ? buildSearch(search) : undefined };

    if (organizationId && organizationId !== 0) {
        where = { ...where, event: { organization_id: { _eq: organizationId } } };
    }
    if (eventId) {
        where = { ...where, event_id: { _eq: eventId } };
    } else if (user?.events) {
        where = { ...where, event_id: { _in: user?.events } };
    }
    if (user?.vendors) {
        where = { ...where, vendor_id: { _in: user?.vendors } };
    }

    let eventWhere: any = {};
    let vendorWhere: any = {};
    if (organizationId) {
        eventWhere.organization_id = { _eq: organizationId };
        eventWhere.is_active = { _eq: true };
        vendorWhere.organization_id = { _eq: organizationId };
    }
    if (user?.events) eventWhere.id = { _in: user.events };
    if (user?.vendors) vendorWhere.id = { _in: user.vendors };

    const locationTypeWhere = organizationId
        ? {
            organization_id: { _in: [0, organizationId] },
            is_active: { _eq: true },
            reference_type: { _eq: "locations_location_type" },
        }
        : {};

    const [{ data: locRes }, { data: eventsRes }, { data: vendorsRes }, { data: ppRes }, { data: locTypeRes }] =
        await Promise.all([
            client.query({ query: GET_LOCATIONS, variables: { where } }),
            client.query({ query: GET_LIST_EVENTS_BY_ORGANIZATION, variables: { where: eventWhere } }),
            client.query({ query: GET_LIST_VENDORS_BY_ORGANIZATION, variables: { where: vendorWhere } }),
            client.query({
                query: GET_LIST_PAYMENT_PROCESSORS_BY_ORGANIZATION,
                variables: { where: organizationId ? { organization_id: { _eq: organizationId } } : {} },
                fetchPolicy: "network-only",
            }),
            client.query({ query: GET_LOCATION_TYPE, variables: { where: locationTypeWhere } }),
        ]);

    return {
        locations: locRes.locations as Location[],
        events: eventsRes.events as Array<{ id: number; name: string }>,
        vendors: vendorsRes.vendors as Array<{ id: number; name: string }>,
        paymentProcessors: ppRes.payment_processor_config as Array<{ id: number; payment_processor: string }>,
        locationTypes: locTypeRes.dashboard_reference as Array<{ reference_label: string; reference_value: string }>,
    };
};

export const LocationList = () => {
    const {editCreatePermission} = usePermissions("locations");
    const {editCreatePermission: paymentProcessorEditPermission} =
        usePermissions("payment_processors");
    const {editCreatePermission: menuEditPermission} = usePermissions("menus");
    const {editCreatePermission: eventsEditPermission} =
        usePermissions("events");
    const {editCreatePermission: vendorsEditPermission} =
        usePermissions("vendors");

    const navigate = useNavigate();
    const revalidator = useRevalidator();
    const gridRef = useRef<GridApi>();
    const originalActiveRef = useRef<Map<string, boolean>>(new Map());

    const { locations, events, vendors, paymentProcessors, locationTypes } = useLoaderData() as {
        locations: Location[];
        events: Array<{ id: number; name: string }>;
        vendors: Array<{ id: number; name: string }>;
        paymentProcessors: Array<{ id: number; payment_processor: string }>;
        locationTypes: Array<{ reference_label: string; reference_value: string }>;
    };

    const getPatchedValue = (p: any, field?: string) => {
        if (!field) return p.value;
        const id = String(p.data?.id ?? "");
        const patch = p.context?.pending?.[id];
        if (patch && Object.prototype.hasOwnProperty.call(patch, field)) {
            return (patch as any)[field];
        }
        return p.data?.[field];
    };

    const [rowData, setRowData] = useState<Location[]>([]);
    const [isActiveToggle, setIsActiveToggle] = useState(true);

    const {pending, pendingCount, resetBaseline, makeEditable, setPending, getBaselineRows} =
        useGridEdit<Location>();
    const getRowId = useCallback((p: { data: Location }) => String(p.data.id), []);

    useEffect(() => {
        resetBaseline(locations);
        originalActiveRef.current = new Map(
            (locations ?? []).map(x => [String(x.id), !!x.is_active])
        );
        setRowData(locations);
    }, [locations, resetBaseline]);

    useEffect(() => {
        const api = gridRef.current;
        if (!api) return;
        const currentModel = api.getFilterModel() ?? {};
        if (isActiveToggle) {
            api.setFilterModel({ ...currentModel, is_active: { filterType: 'text', type: 'equals', filter: 'Yes' } });
        } else {
            const { is_active, ...rest } = currentModel;
            api.setFilterModel(Object.keys(rest).length > 0 ? rest : null);
        }
        api.onFilterChanged();
    }, [isActiveToggle, pendingCount]);

    useEffect(() => {
        gridRef.current?.refreshCells({force: true});
        gridRef.current?.refreshClientSideRowModel("filter");
    }, [pending]);

    const locationNameOptions = useMemo(
        () =>
            Array.from(
                new Set((locations ?? []).map((l) => String(l.name ?? "").trim()).filter(Boolean))
            ).sort((a, b) => a.localeCompare(b)),
        [locations]
    );

    const linkRenderer = useMemo(
        () => linkRendererFactory<Location>(!!editCreatePermission, (p) => `/locations/${p.data.id}/edit`),
        [editCreatePermission]
    );
    const eventLinkRenderer = useMemo(
        () => linkRendererFactory<Location>(!!eventsEditPermission, (p) => `/events/${p.data?.event?.id}/edit`),
        [eventsEditPermission]
    );
    const vendorLinkRenderer = useMemo(
        () => linkRendererFactory<Location>(!!vendorsEditPermission, (p) => `/vendors/${p.data?.vendor?.id}/edit`),
        [vendorsEditPermission]
    );
    const ppLinkRenderer = useMemo(
        () =>
            linkRendererFactory<Location>(
                !!paymentProcessorEditPermission,
                (p) => `/payment-processors/${p.data?.payment_processor_config?.id ?? p.data?.payment_processor_id}/edit`
            ),
        [paymentProcessorEditPermission]
    );

    const menusRenderer = useMemo(
        () =>
            multiMenuLinksRenderer(
                !!menuEditPermission,
                (lm) => `/menus/${lm?.menus?.id ?? lm.menus?.id}/edit`
            ),
        [menuEditPermission]
    );

    const locTypeList = useMemo(
        () =>
            (locationTypes ?? [])
                .map(t => ({
                    value: String(t.reference_value ?? "").trim(),
                    label: String(t.reference_label ?? "").trim(),
                }))
                .filter(t => t.value && t.label)
                .sort((a, b) => a.label.localeCompare(b.label)),
        [locationTypes]
    );
    const ltValueToLabel = useMemo(() => new Map(locTypeList.map(t => [t.value, t.label] as const)), [locTypeList]);
    const ltLabelToValue = useMemo(() => new Map(locTypeList.map(t => [t.label, t.value] as const)), [locTypeList]);

    const eventList = useMemo(
        () => (events ?? []).map(e => ({ id: Number(e.id), name: String(e.name ?? "").trim() }))
            .filter(e => e.name).sort((a,b) => a.name.localeCompare(b.name)),
        [events]
    );
    const eventIdToName = useMemo(() => new Map(eventList.map(e => [e.id, e.name] as const)), [eventList]);
    const eventNameToId = useMemo(() => new Map(eventList.map(e => [e.name, e.id] as const)), [eventList]);

    const vendorList = useMemo(
        () => (vendors ?? []).map(v => ({ id: Number(v.id), name: String(v.name ?? "").trim() }))
            .filter(v => v.name).sort((a,b) => a.name.localeCompare(b.name)),
        [vendors]
    );
    const vendorIdToName = useMemo(() => new Map(vendorList.map(v => [v.id, v.name] as const)), [vendorList]);
    const vendorNameToId = useMemo(() => new Map(vendorList.map(v => [v.name, v.id] as const)), [vendorList]);

    const ppList = useMemo(
        () => (paymentProcessors ?? []).map(p => ({ id: Number(p.id), name: String(p.payment_processor ?? "").trim() }))
            .filter(p => p.name).sort((a,b) => a.name.localeCompare(b.name)),
        [paymentProcessors]
    );
    const ppIdToName = useMemo(() => new Map(ppList.map(p => [p.id, p.name] as const)), [ppList]);
    const ppNameToId = useMemo(() => new Map(ppList.map(p => [p.name, p.id] as const)), [ppList]);

    const colDefs: ColDef<Location>[] = useMemo(() => {
        return [
            {
                headerName: "Id",
                valueGetter: (p) => p.data?.id,
                cellRenderer: linkRenderer,
                filter: "agTextColumnFilter",
                pinned: "left",
            },
            {
                headerName: "Location Name",
                field: "name",
                cellEditor: "agTextCellEditor",
                ...makeEditable(
                    "name",
                    {
                        type: "text",
                        canEdit: !!editCreatePermission,
                        formatter: (v) => String(v ?? "").trim(),
                        parser: (nv) => String(nv ?? "").trim(),
                        equals: (a, b) => String(a ?? "").trim() === String(b ?? "").trim(),
                    },
                    setRowData
                ),
                filter: "agTextColumnFilter",
                pinned: "left",
            },
            {
                headerName: "Is Active?",
                field: "is_active",
                cellDataType: "text",
                sortable: true,
                filter: "agTextColumnFilter",
                ...makeEditable(
                    "is_active",
                    {type: "boolean", canEdit: !!editCreatePermission, toggleOnClick: true},
                    setRowData
                ),

                valueFormatter: (p) => (getPatchedValue(p, "is_active") ? "Yes" : "No"),

                filterValueGetter: (p: any) => {
                    const id = String(p.data?.id ?? "");
                    const patch = p.context?.pending?.[id] as { is_active?: boolean } | undefined;

                    if (patch && Object.prototype.hasOwnProperty.call(patch, "is_active")) {
                        return patch.is_active ? "Yes" : "No";
                    }

                    const baseline = originalActiveRef.current.get(id);
                    return baseline ? "Yes" : "No";
                },
            },
            {
                headerName: "Location Type",
                field: "location_type",
                cellEditor: TextSuggestEditor,
                cellEditorParams: (p: { value: any; data: Location }) => ({
                    values: locTypeList.map(t => t.label),
                    showAllOnEdit: true,
                    initialValue:
                        ltValueToLabel.get(String(p.value ?? p.data?.location_type ?? "").trim()) ??
                        String(p.value ?? p.data?.location_type ?? ""),
                }),
                ...makeEditable(
                    "location_type",
                    {
                        type: "text",
                        canEdit: !!editCreatePermission,
                        parser: (nv: any, ov: any) => {
                            const s = String(nv ?? "").trim();
                            if (!s) return ov;
                            return ltLabelToValue.get(s) ?? s;
                        },
                        formatter: (v: any) =>
                            ltValueToLabel.get(String(v ?? "").trim()) ?? String(v ?? "").trim(),
                        equals: (a: any, b: any) => String(a ?? "").trim() === String(b ?? "").trim(),
                    },
                    setRowData
                ),
                filter: "agTextColumnFilter",
                filterValueGetter: (p) =>
                    ltValueToLabel.get(String((p.data as Location).location_type ?? "").trim()) ??
                    String((p.data as Location).location_type ?? "").trim(),
            },
            {
                headerName: "Event Name",
                valueGetter: (p) => eventIdToName.get(Number(p.data?.event_id ?? (p as any).data?.event?.id)) ?? (p as any).data?.event?.name,
                cellRenderer: eventLinkRenderer,
                cellEditor: TextSuggestEditor,
                cellEditorParams: (p: { data: Location }) => ({
                    values: eventList.map(e => e.name),
                    showAllOnEdit: true,
                    initialValue: eventIdToName.get(Number(p.data?.event_id ?? (p as any).data?.event?.id)) ?? (p as any).data?.event?.name ?? "",
                }),
                ...makeEditable(
                    "event_id" as any,
                    {
                        type: "text",
                        canEdit: !!editCreatePermission,
                        parser: (nv: any, ov: any) => {
                            const s = String(nv ?? "").trim();
                            if (!s) return ov;
                            const byName = eventNameToId.get(s);
                            if (byName != null) return byName;
                            const n = Number(s);
                            return (!Number.isNaN(n) && eventIdToName.has(n)) ? n : ov;
                        },
                        formatter: (v: any) => String(v ?? "").trim(),
                        equals: (a: any, b: any) => Number(a ?? 0) === Number(b ?? 0),
                    },
                    setRowData
                ),
                filter: "agTextColumnFilter",
                filterValueGetter: (p) => {
                    const id = Number((p.data as Location).event_id ?? (p.data as any)?.event?.id);
                    return eventIdToName.get(id) ?? (p.data as any)?.event?.name ?? "";
                },
            },
            {
                headerName: "Vendor Name",
                valueGetter: (p) => vendorIdToName.get(Number(p.data?.vendor_id ?? (p as any).data?.vendor?.id)) ?? (p as any).data?.vendor?.name,
                cellRenderer: vendorLinkRenderer,
                cellEditor: TextSuggestEditor,
                cellEditorParams: (p: { data: Location }) => ({
                    values: vendorList.map(v => v.name),
                    showAllOnEdit: true,
                    initialValue: vendorIdToName.get(Number(p.data?.vendor_id ?? (p as any).data?.vendor?.id)) ?? (p as any).data?.vendor?.name ?? "",
                }),
                ...makeEditable(
                    "vendor_id" as any,
                    {
                        type: "text",
                        canEdit: !!editCreatePermission,
                        parser: (nv: any, ov: any) => {
                            const s = String(nv ?? "").trim();
                            if (!s) return ov;
                            const byName = vendorNameToId.get(s);
                            if (byName != null) return byName;
                            const n = Number(s);
                            return (!Number.isNaN(n) && vendorIdToName.has(n)) ? n : ov;
                        },
                        formatter: (v: any) => String(v ?? "").trim(),
                        equals: (a: any, b: any) => Number(a ?? 0) === Number(b ?? 0),
                    },
                    setRowData
                ),
                filter: "agTextColumnFilter",
                filterValueGetter: (p) => {
                    const id = Number((p.data as Location).vendor_id ?? (p.data as any)?.vendor?.id);
                    return vendorIdToName.get(id) ?? (p.data as any)?.vendor?.name ?? "";
                },
            },
            {
                headerName: "Payment Processor",
                valueGetter: (p) => {
                    const id = Number((p.data as any)?.payment_processor_id ?? (p.data as any)?.payment_processor_config?.id);
                    return ppIdToName.get(id) ?? (p.data as any)?.payment_processor_config?.payment_processor ?? "";
                },
                cellRenderer: ppLinkRenderer,
                cellEditor: TextSuggestEditor,
                cellEditorParams: (p: { data: Location }) => ({
                    values: ppList.map(pp => pp.name),
                    showAllOnEdit: true,
                    initialValue: (() => {
                        const id = Number((p.data as any)?.payment_processor_id ?? (p.data as any)?.payment_processor_config?.id);
                        return ppIdToName.get(id) ?? (p.data as any)?.payment_processor_config?.payment_processor ?? "";
                    })(),
                }),
                ...makeEditable(
                    "payment_processor_id" as any,
                    {
                        type: "text",
                        canEdit: !!editCreatePermission,
                        parser: (nv: any, ov: any) => {
                            const s = String(nv ?? "").trim();
                            if (!s) return ov;
                            const byName = ppNameToId.get(s);
                            if (byName != null) return byName;
                            const n = Number(s);
                            return (!Number.isNaN(n) && ppIdToName.has(n)) ? n : ov;
                        },
                        formatter: (v: any) => String(v ?? "").trim(),
                        equals: (a: any, b: any) => Number(a ?? 0) === Number(b ?? 0),
                    },
                    setRowData
                ),
                filter: "agTextColumnFilter",
                filterValueGetter: (p) => {
                    const id = Number((p.data as any)?.payment_processor_id ?? (p.data as any)?.payment_processor_config?.id);
                    return ppIdToName.get(id) ?? (p.data as any)?.payment_processor_config?.payment_processor ?? "";
                },
            },
            {
                headerName: "Service Fee %",
                field: "digital_surcharge_percentage",
                ...makeEditable(
                    "digital_surcharge_percentage",
                    {
                        type: "text",
                        canEdit: !!editCreatePermission,
                        parser: (nv: any, ov: any) => {
                            const s = String(nv ?? "").trim();
                            if (!s) return ov;
                            const num = Number(s.replace(/[^0-9.-]/g, ""));
                            if (Number.isNaN(num)) return ov;
                            const clamped = Math.max(0, Math.min(100, num));
                            return clamped;
                        },
                        formatter: (v: any) => {
                            const n = Number(v ?? 0);
                            return Number.isFinite(n) && n !== 0 ? `${n}%` : "";
                        },
                        equals: (a: any, b: any) => Number(a ?? 0) === Number(b ?? 0),
                    },
                    setRowData
                ),
                valueFormatter: (p) => {
                    const v = Number(p.value ?? 0);
                    return Number.isFinite(v) && v !== 0 ? `${v}%` : "";
                },
                filter: "agTextColumnFilter",
                filterValueGetter: (p) => {
                    const v = Number(getPatchedValue(p, "digital_surcharge_percentage") ?? 0);
                    return Number.isFinite(v) && v !== 0 ? `${v}%` : "";
                },
            },

            {
                headerName: "Dynamic Descriptor",
                field: "dynamic_descriptor",
                ...makeEditable(
                    "dynamic_descriptor",
                    {
                        type: "text",
                        canEdit: !!editCreatePermission,
                        // The location form enforces maxLength 22 + sanitize on
                        // this column; the grid enforced neither, so the two
                        // editors accepted different values for the same field.
                        parser: (nv: any) =>
                            String(nv ?? "")
                                .replace(/[^a-zA-Z0-9 ]/g, "")
                                .trim()
                                .slice(0, 22),
                    },
                    setRowData
                ),
                filter: "agTextColumnFilter",
            },

            {
                headerName: "Menu(s) Name",
                valueGetter: (p) => p.data?.location_menus,
                valueFormatter: (params) => {
                    const menus = (params.value as Location["location_menus"]) || [];
                    return menus
                        .map((m: any) => m?.menu?.name ?? m?.menus?.name ?? "")
                        .filter(Boolean)
                        .join(", ");
                },
                cellRenderer: menusRenderer,
                filter: "agTextColumnFilter",
                filterValueGetter: (p) => {
                    const menus = (p.data as Location)?.location_menus ?? [];
                    return menus
                        .map((m: any) => m?.menu?.name ?? m?.menus?.name ?? "")
                        .filter(Boolean)
                        .join(", ");
                },
            },

            {
                headerName: "Clerks Required?",
                field: "users_required",
                ...makeEditable(
                    "users_required",
                    {type: "boolean", canEdit: !!editCreatePermission, toggleOnClick: true},
                    setRowData
                ),
                valueFormatter: (p) => (p.value ? "Yes" : "No"),
                filter: "agTextColumnFilter",
                filterValueGetter: (p) => (getPatchedValue(p, "users_required") ? "Yes" : "No"),
            },
            {
                headerName: "Clerk Passwords Required?",
                field: "users_password_required",
                ...makeEditable(
                    "users_password_required",
                    {type: "boolean", canEdit: !!editCreatePermission, toggleOnClick: true},
                    setRowData
                ),
                valueFormatter: (p) => (p.value ? "Yes" : "No"),
                filter: "agTextColumnFilter",
                filterValueGetter: (p) => (getPatchedValue(p, "users_password_required") ? "Yes" : "No"),
            },

            {
                headerName: "Updated By",
                valueGetter: (p) => p.data?.user?.username,
                filter: "agTextColumnFilter",
            },
            {
                headerName: "Created Date",
                valueGetter: (p) => p.data?.created_at,
                valueFormatter: (p) =>
                    p.value ? moment(p.value).format("MM/DD/YYYY h:mm:ss a") : "",
                filter: "agTextColumnFilter",
                filterValueGetter: (p) =>
                    p.data?.created_at ? moment(p.data.created_at).format("MM/DD/YYYY h:mm:ss a") : "",
            },
            {
                headerName: "Updated Date",
                valueGetter: (p) => p.data?.updated_at,
                valueFormatter: (p) =>
                    p.value ? moment(p.value).format("MM/DD/YYYY h:mm:ss a") : "",
                filter: "agTextColumnFilter",
                filterValueGetter: (p) =>
                    p.data?.updated_at ? moment(p.data.updated_at).format("MM/DD/YYYY h:mm:ss a") : "",
            },
        ];
    },  [
        editCreatePermission,
        linkRenderer,
        eventLinkRenderer,
        vendorLinkRenderer,
        ppLinkRenderer,
        menusRenderer,
        makeEditable,
        setRowData,
        locTypeList, ltValueToLabel, ltLabelToValue,
        eventList, eventIdToName, eventNameToId,
        vendorList, vendorIdToName, vendorNameToId,
        ppList, ppIdToName, ppNameToId,
    ]);

    const defaultColDef = useMemo(() => ({
        valueGetter: (p: any) => p.colDef.field ? getPatchedValue(p, p.colDef.field) : p.value,
        // RP-2583: columns stay put. Dragging a header (easy to do by accident on a phone)
        // used to reorder columns or pull them off the grid.
        suppressMovable: true,
    }), []);

    const rowHeight = 50;
    const autoSizeStrategy: SizeColumnsToContentStrategy = {type: "fitCellContents"};
    const gridOptions = useMemo(() => ({enableCellTextSelection: true, context: {pending}}), [pending]);

    const handleExportCSV = () => {
        if (gridRef.current) {
            const timestamp = new Date().toISOString().replace(/[-:]/g, "");
            gridRef.current.exportDataAsCsv({
                fileName: `Locations_${timestamp}.csv`,
                columnSeparator: ",",
            });
        }
    };

    const mutateRow = async (id: number, patch: Partial<Location>) => {
        const user = getUserStore().user;
        const input = {
            ...patch,
            last_updated_by: user?.id,
            updated_at: new Date().toISOString()
        };
        const res = await client.mutate({
            mutation: UPDATE_LOCATION,
            variables: { id, input },
            errorPolicy: "all",
        });

        if (res.errors?.length) {
            throw new Error(res.errors.map((e) => e.message).join("; "));
        }
        const updatedId = res.data?.update_locations_by_pk?.id;
        if (!updatedId) throw new Error(`No id returned for ${id}`);
    };

    const { handleSave } = useGridSave<Location>({
        rowData,
        setRowData,
        pending,
        setPending,
        resetBaseline,
        gridApiRef: gridRef as any,
        revalidate: () => revalidator.revalidate(),
        mutateRow,
        optimistic: true,
        baselines: [
            { ref: originalActiveRef, value: (r) => !!r.is_active },
        ],
    });

    return (
        <div className="w-full flex flex-col overflow-hidden" style={{ height: "calc(100vh - 128px)" }}>
            <div className="md:hidden grid grid-cols-6 min-w-0 px-0 py-0">
                <div className="col-span-6 flex items-center justify-between min-h-[48px] mb-1">
                    <div className="flex items-center gap-2 pl-2 pt-2">
                        <GridEditActions
                            pendingCount={pendingCount}
                            pending={pending}
                            gridRef={gridRef}
                            resetBaseline={resetBaseline}
                            baselineData={locations}
                            isActiveToggle={isActiveToggle}
                            setRowData={setRowData}
                            setPending={setPending}
                            onSave={handleSave}
                            getBaselineRows={getBaselineRows}
                        />
                    </div>

                    <div className="ml-2 flex flex-col items-center">
                        <span className="block text-xs font-medium text-gray-500 whitespace-nowrap">Active Only</span>
                        <Switch
                            checked={isActiveToggle}
                            onChange={() => setIsActiveToggle((prev) => !prev)}
                            onColor="#EE2E30"
                            onHandleColor="#FFFFFF"
                            handleDiameter={18}
                            uncheckedIcon={false}
                            checkedIcon={false}
                            boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                            activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                            height={15}
                            width={48}
                        />
                    </div>
                </div>

                <div className="col-span-6 flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                        <div className="w-full px-3 py-2">
                            <Input
                                name="search"
                                placeholder="Search Locations"
                                onChange={(s) => {
                                    navigate(s && typeof s === "string" && s.length > 0 ? `/locations/list?search=${s}` : `/locations/list`);
                                    revalidator.revalidate();
                                }}
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                        <SecondaryButton onClick={handleExportCSV}>Export to CSV</SecondaryButton>
                        {editCreatePermission && (
                            <PrimaryButton onClick={() => navigate(`/locations/create`)}>Create</PrimaryButton>
                        )}
                    </div>
                </div>
            </div>

            <div className="hidden md:grid grid-cols-12 items-center gap-3 min-w-0 py-2">
                <div className="col-span-5 min-w-0">
                    <div className="w-full">
                        <Input
                            name="search"
                            placeholder="Search Locations"
                            onChange={(s) => {
                                navigate(s && typeof s === "string" && s.length > 0 ? `/locations/list?search=${s}` : `/locations/list`);
                                revalidator.revalidate();
                            }}
                        />
                    </div>
                </div>

                <div className="col-span-3 col-start-6 justify-self-start flex items-center gap-1">
                    <GridEditActions
                        pendingCount={pendingCount}
                        pending={pending}
                        gridRef={gridRef}
                        resetBaseline={resetBaseline}
                        baselineData={locations}
                        isActiveToggle={isActiveToggle}
                        setRowData={setRowData}
                        setPending={setPending}
                        onSave={handleSave}
                        getBaselineRows={getBaselineRows}
                    />
                </div>

                <div className="col-span-1 flex flex-col items-center">
                    <span className="block text-sm font-medium text-gray-500 whitespace-nowrap">Active Only</span>
                    <Switch
                        checked={isActiveToggle}
                        onChange={() => setIsActiveToggle((prev) => !prev)}
                        onColor="#EE2E30"
                        onHandleColor="#FFFFFF"
                        handleDiameter={18}
                        uncheckedIcon={false}
                        checkedIcon={false}
                        boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                        activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                        height={15}
                        width={48}
                    />
                </div>

                <div className="col-span-3 flex justify-end items-center gap-1 whitespace-nowrap">
                    <SecondaryButton onClick={handleExportCSV}>
                        <span className="whitespace-nowrap">Export&nbsp;to&nbsp;CSV</span>
                    </SecondaryButton>
                    {editCreatePermission && (
                        <PrimaryButton onClick={() => navigate(`/locations/create`)}>Create</PrimaryButton>
                    )}
                </div>
            </div>

            <div className="flex-1 min-h-0">
                <div className="ag-theme-quartz w-full h-full min-w-0">
                    <AgGridReact
                        rowData={rowData}
                        columnDefs={colDefs}
                        defaultColDef={defaultColDef}
                        suppressDragLeaveHidesColumns
                        rowHeight={rowHeight}
                        autoSizeStrategy={autoSizeStrategy}
                        pagination
                        gridOptions={gridOptions}
                        paginationPageSize={100}
                        getRowId={getRowId}
                        onGridReady={(params) => {
                            gridRef.current = params.api;
                            if (isActiveToggle) {
                                const currentModel = params.api.getFilterModel() ?? {};
                                params.api.setFilterModel({ ...currentModel, is_active: { filterType: 'text', type: 'equals', filter: 'Yes' } });
                                params.api.onFilterChanged();
                            }
                        }}
                        singleClickEdit
                        stopEditingWhenCellsLoseFocus
                    />
                </div>
            </div>
        </div>
    );
};
